<script setup lang="ts">
import type { MatchTab } from "@/types/fileImport"
import {
  DialogSize,
  KDropdown,
  KSelectWrapper,
  KToggle,
} from "@kaleidoscope-solutions/ks-design-system"
import { QuestionIcon } from "@kaleidoscope-solutions/ks-icons/vue/fill"
import { ArrowLongRightIcon } from "@kaleidoscope-solutions/ks-icons/vue/light"
import { computedAsync } from "@vueuse/core"
import { storeToRefs } from "pinia"
import { computed, toRef, watch } from "vue"
import { openDialog } from "vue3-promise-dialog"
import MissingInfoImage from "@/assets/pngs/Confused.png"
import PictogramDialog from "@/components/Dialog/PictogramDialog.vue"
import MatchTable from "@/components/FileImport/Match/MatchTable.vue"
import StatusDecoration from "@/components/FileImport/StatusDecoration.vue"
import { useFileImportStore } from "@/stores/fileImport"
import { Decorator, ItemMatchStatus } from "@/types"
import useAnalytics from "@/utils/analytics"
import { DirtyState } from "@/utils/fileImport/interactions"

interface Props {
  /**
   * Tab Items
   */
  tab: MatchTab
}

const props = defineProps<Props>()

const fileImportStore = useFileImportStore()

const { trackEvent } = useAnalytics()

const { itemMatchInfo, dateFormatMap } = storeToRefs(fileImportStore)

const dateFormats = await fileImportStore.dateFormats()

const categoryName = toRef(props.tab, "title", "")

const manualDateMatch = computed({
  get() {
    return !dateFormatMap.value.has(categoryName.value)
  },
  set(value: boolean) {
    if (value) {
      dateFormatMap.value.delete(categoryName.value)
    } else {
      dateFormatMap.value.set(categoryName.value, "")
    }
  },
})

const selectedDateFormat = computed<string>({
  get() {
    return dateFormatMap.value.get(categoryName.value) ?? ""
  },
  set(dateFormat: string) {
    dateFormatMap.value.set(categoryName.value, dateFormat)
  },
})

const dateFormatOptions = computed(() => {
  const chunk = 4

  // Split array into chunks to add dividing line between items.
  return dateFormats.reduce((all, one, i) => {
    const ch = Math.floor(i / chunk)
    all[ch] = [].concat(all[ch] || [], one)
    return all
  }, [])
})
const categoryItems = computed(
  () => itemMatchInfo.value.get(categoryName.value) ?? []
)

const dropDownOptions = computedAsync(async () => {
  if (!manualDateMatch.value) {
    return []
  }
  return await fileImportStore.getCategoryItems(categoryName.value)
}, [])

const disableDropdowns = computed(() => !manualDateMatch.value)

function handleIgnore(rowIndex: number) {
  fileImportStore.setMatchToIgnore(categoryName.value, rowIndex)
}

async function handleUpdate(rowIndex: number, itemName: string) {
  const item = categoryItems.value[rowIndex]

  const status = await fileImportStore.figureOutUpdatedStatus(
    categoryName.value,
    item,
    itemName
  )

  if (status === ItemMatchStatus.EDIT_MATCH) {
    fileImportStore.editBanner = true
    fileImportStore.editItems = {
      externalName: item.externalName,
      previousName: item.internalName as string,
      newName: itemName,
    }
  }

  fileImportStore.setMatchStatus(categoryName.value, rowIndex, itemName, status)
}

const hasSomeMatched = computed(() =>
  categoryItems.value.some((item) => item.status !== ItemMatchStatus.PENDING)
)

async function updateFormatMatch(match: boolean) {
  if (match) {
    // on toggle of manual match, see if possible can auto date match again
    fileImportStore.populateMatchStatus(categoryName.value)
  }

  if (!match && hasSomeMatched.value) {
    const ok = await openDialog<any>(PictogramDialog, {
      open: true,
      size: DialogSize.medium,
      confirmDialog: true,
      okButton: "Switch off and lose manual input",
      imagePath: MissingInfoImage,
      title: "Warning. Lose manual inputs",
      body: [
        `This will discard manual changes you have made. Just be mindful that you will <strong class="font-bold"> lose the manual input </strong> you have provided so far.`,
      ],
    })

    if (ok) {
      fileImportStore.resetMatches(categoryName.value)
      manualDateMatch.value = !manualDateMatch.value
    }
  } else {
    manualDateMatch.value = match
    fileImportStore.resetMatches(categoryName.value)
  }
}

watch(
  () => selectedDateFormat?.value,
  async (newValue, oldValue) => {
    // Reset matches when manual match is turned on.
    if (selectedDateFormat?.value === "") {
      fileImportStore.resetMatches(categoryName.value)
      return
    }

    trackEvent(
      "GDI_date_format_changed",
      {
        "date format old/new": `${oldValue} | ${newValue}`,
        category: "File Import",
        action: "Date format changed",
        label: categoryName.value,
      }
    )

    const parsed = await fileImportStore.parsedDates(
      categoryName.value,
      selectedDateFormat.value
    )
    fileImportStore.setDateMatches(categoryName.value, parsed)
    fileImportStore.matchTabInteractions.markDirty(
      categoryName.value,
      DirtyState.Dirty
    )
  }
)

const tooltip = {
  manualMatch:
    "If you cannot find the option to bulk match your dates, you can turn the toggle on and match each date manually.",
  inFile: "Date format present in the imported file",
  inKaleidoscope: "Date format present in Kaleidoscope",
}

watch(
  () => props.tab.decorator(),
  () => {
    if (
      selectedDateFormat?.value === "" &&
      fileImportStore.isCategoryMatched(categoryName.value)
    ) {
      manualDateMatch.value = true
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="mt-1 flex gap-4">
    <p>
      Manual match each row
      <QuestionIcon
        v-tooltip="tooltip.manualMatch"
        class="inline-block size-4"
      />
    </p>
    <KToggle
      id="timeMatchToggle"
      :model-value="manualDateMatch"
      @update:model-value="(value) => updateFormatMatch(!!value)"
    />
  </div>
  <div
    v-if="!manualDateMatch"
    class="flex items-start gap-4"
  >
    <div class="flex max-w-72 flex-1 flex-col gap-2 pl-1">
      <div class="flex items-center gap-2">
        <label>Format in the file</label>
        <QuestionIcon
          v-tooltip="tooltip.inFile"
          class="size-4"
        />
        <StatusDecoration :decorator="tab.decorator()" />
      </div>

      <KDropdown
        v-model="selectedDateFormat"
        :items="dateFormatOptions"
        :allow-search="false"
        :nullable="false"
      >
        <template #selected="{ selected, open }">
          <div class="w-full">
            <KSelectWrapper :open="open">
              {{ selected }}
            </KSelectWrapper>
          </div>
        </template>
      </KDropdown>
    </div>
    <div class="mt-8 flex h-9 items-center">
      <ArrowLongRightIcon class="size-6" />
    </div>
    <div class="flex max-w-56 flex-1 flex-col gap-2">
      <div class="flex items-center gap-2">
        <label>Format in Kaleidoscope</label>
        <QuestionIcon
          v-tooltip="tooltip.inKaleidoscope"
          class="size-4"
        />
      </div>

      <div
        class="h-10 rounded-md border border-warmgray-700 bg-warmgray-50 px-2 py-1.5 font-bold"
      >
        YYYY-MM-DD
      </div>
    </div>
  </div>

  <MatchTable
    class="w-fit"
    :items="categoryItems"
    :options="dropDownOptions"
    :show-invalid="
      tab.decorator() === Decorator.INVALID && selectedDateFormat === ''
    "
    :disable-dropdowns="disableDropdowns"
    @ignore="handleIgnore"
    @update="handleUpdate"
  />
</template>
